import { useMemo, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Col, Row } from "react-grid-system";
import { theme } from "../../../theme";
import DonutStatSection from "../../molecules/assessment/DonutStatSection";
import FilterChip from "../../atoms/inputs/FilterChip";
import { HorizontalSpacer } from "../../atoms/Spacers";
import { formatPrice } from "../../../utils/formatters";
import type {
  ClientRelationshipDoneInvoices,
  ClientRelationshipOpenInvoices,
  ClientRelationshipStatusTotal,
} from "../../../models/ClientRelationshipModel";

type StatusConfig<T> = Array<{ key: keyof T; color: string }>;

const DONE_STATUSES: StatusConfig<ClientRelationshipDoneInvoices> = [
  { key: "paid", color: theme.colors.success },
  { key: "directPayment", color: theme.colors.success20 },
  { key: "disputed", color: theme.colors.error20 },
  { key: "overduePerAgreement", color: theme.colors.info },
  { key: "recourseOther", color: theme.colors.warning },
  { key: "bankruptcy", color: theme.colors.errorDark },
  { key: "internalMishandling", color: theme.colors.error20 },
];

const OPEN_STATUSES: StatusConfig<ClientRelationshipOpenInvoices> = [
  { key: "open", color: theme.colors.info },
  { key: "overdue", color: theme.colors.error },
  { key: "debtCollection", color: theme.colors.errorDark },
  { key: "enforcementWarning", color: theme.colors.warning },
];

const DEFAULT_CURRENCY = "SEK";

const ChipGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

interface Props {
  doneInvoices?: ClientRelationshipDoneInvoices;
  openInvoices?: ClientRelationshipOpenInvoices;
}

const getAmount = (
  total: ClientRelationshipStatusTotal | undefined,
  currency: string,
) =>
  total?.amountsByCurrency.find((amount) => amount.currencyCode === currency)
    ?.totalAmount ?? 0;

const getCurrencies = (groups: Array<object | undefined>) => {
  const currencies = new Set<string>();
  groups.forEach((group) =>
    Object.values(group ?? {}).forEach((total: ClientRelationshipStatusTotal) =>
      total?.amountsByCurrency?.forEach((amount) =>
        currencies.add(amount.currencyCode),
      ),
    ),
  );
  return [...currencies].sort((a, b) =>
    a === DEFAULT_CURRENCY
      ? -1
      : b === DEFAULT_CURRENCY
        ? 1
        : a.localeCompare(b),
  );
};

const RelationshipInvoiceCharts: FC<Props> = ({
  doneInvoices,
  openInvoices,
}) => {
  const { t } = useTranslation();
  const [selectedCurrency, setSelectedCurrency] = useState<string>();

  const currencies = useMemo(
    () => getCurrencies([doneInvoices, openInvoices]),
    [doneInvoices, openInvoices],
  );
  const currency =
    selectedCurrency && currencies.includes(selectedCurrency)
      ? selectedCurrency
      : (currencies[0] ?? DEFAULT_CURRENCY);

  const toDonutSection = <T,>(
    group: T | undefined,
    statuses: StatusConfig<T>,
  ) => {
    const entries = statuses.map(({ key, color }) => {
      const total = group?.[key] as ClientRelationshipStatusTotal | undefined;
      return {
        id: String(key),
        titleKey: t(`invoiceAssessment.relationshipStatus.${String(key)}`),
        amount: getAmount(total, currency),
        invoiceCount: total?.invoiceCount ?? 0,
        color,
      };
    });
    const totalAmount = entries.reduce((sum, entry) => sum + entry.amount, 0);
    const invoiceCount = entries.reduce(
      (sum, entry) => sum + entry.invoiceCount,
      0,
    );

    return {
      subtitle: invoiceCount
        ? t("invoiceAssessment.relationshipInvoiceCount", {
            count: invoiceCount,
          })
        : t("invoiceAssessment.relationshipNoInvoices"),
      data: entries
        .filter((entry) => entry.amount !== 0)
        .map(({ id, titleKey, amount, color }) => ({
          id,
          titleKey,
          color,
          percentage: totalAmount ? (amount / totalAmount) * 100 : 0,
          valueText: `${formatPrice(amount, 0)} ${currency}`,
        })),
    };
  };

  const done = toDonutSection(doneInvoices, DONE_STATUSES);
  const open = toDonutSection(openInvoices, OPEN_STATUSES);

  return (
    <>
      {currencies.length > 1 && (
        <>
          <ChipGroup
            role="group"
            aria-label={t("invoiceAssessment.relationshipCurrency")}
          >
            {currencies.map((code) => (
              <FilterChip
                key={code}
                titleKey={code}
                selected={code === currency}
                onSelect={() => setSelectedCurrency(code)}
              />
            ))}
          </ChipGroup>
          <HorizontalSpacer spacing={1} />
        </>
      )}
      <Row>
        <Col lg={6}>
          <DonutStatSection
            colorType="status"
            title={t("invoiceAssessment.doneInvoices")}
            subtitle={done.subtitle}
            data={done.data}
          />
        </Col>
        <Col lg={6}>
          <DonutStatSection
            colorType="status"
            title={t("invoiceAssessment.openInvoices")}
            subtitle={open.subtitle}
            data={open.data}
          />
        </Col>
      </Row>
    </>
  );
};

export default RelationshipInvoiceCharts;
